import {Trans} from '@lingui/macro';
import {keyBy} from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {computeMultiSelectCheckboxState} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {TableEmptyState} from '../../components/common/table/tableEmptyState';
import {TableMultiSelectHeaderRow} from '../../components/common/table/tableMultiSelectHeaderRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {CircleModel} from '../../models/api/circleModel';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {circlesTableColumns} from './circlesTableConfig';
import {CircleTableRow} from './circleTableRow';

/*
 * Consts.
 */

const headerOffset =
  36 /** header margin */ +
  55 /** header height */ +
  30 /** search input height */ +
  16 /** search input margin */ +
  40 /** table header height */ +
  15; /** offset */

/*
 * Props.
 */

interface CirclesTableProps {
  circles: readonly CircleModel[] | undefined;
  moreStatus: AsyncMoreStatusesEnum;
  onLoadMore: Handler;
  onDeleteCircles: Handler;

  selectedCircleIds: ReadonlyArray<number>;
  onSelectedCircleIds: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Component.
 */

export const CirclesTable: React.FC<CirclesTableProps> = (props) => {
  const {
    circles = [],
    moreStatus,
    onLoadMore,
    onDeleteCircles,
    selectedCircleIds,
    onSelectedCircleIds,
  } = props;

  const onSelect = useCallback(
    (circleId: number) => {
      if (selectedCircleIds.includes(circleId)) {
        onSelectedCircleIds(selectedCircleIds.filter((circle) => circle !== circleId));
        return;
      }

      onSelectedCircleIds([...selectedCircleIds, circleId]);
    },
    [onSelectedCircleIds, selectedCircleIds],
  );

  const isCircleSelected = useCallback(
    (circleId?: number) => circleId !== undefined && selectedCircleIds.includes(circleId),
    [selectedCircleIds],
  );

  const circlesIds = useMemo(() => circles.map((circle) => circle.id), [circles]);
  const circlesById = useMemo(() => keyBy(circles, 'id'), [circles]);

  const onMultiselectClick = useCallback(
    (isSelectingAll: boolean) => {
      if (isSelectingAll) {
        onSelectedCircleIds(circlesIds);
        return;
      }

      onSelectedCircleIds([]);
    },
    [circlesIds, onSelectedCircleIds],
  );

  const renderRow: VirtualTableRowRenderer<number> = useCallback(
    (circleId, options, siblingData) => (
      <CircleTableRow
        options={options}
        circle={circlesById[circleId]}
        isInMultiSelectMode={selectedCircleIds.length !== 0}
        isSelected={isCircleSelected(circleId)}
        onSelect={() => onSelect(circleId)}
        shouldRemoveTopBorderRadius={isCircleSelected(siblingData?.previous)}
        shouldRemoveBottomBorderRadius={isCircleSelected(siblingData?.next)}
      />
    ),
    [circlesById, isCircleSelected, onSelect, selectedCircleIds.length],
  );

  const renderMultiSelectHeader = useCallback(
    (onMultiSelectHeaderChange: HandlerOf<boolean>) =>
      renderHeader(onMultiSelectHeaderChange, selectedCircleIds, circlesIds, onDeleteCircles),
    [circlesIds, onDeleteCircles, selectedCircleIds],
  );

  return (
    <StaticVirtualTable<number>
      items={circlesIds}
      columns={circlesTableColumns}
      hasData={moreStatus !== AsyncMoreStatusesEnum.LOADING && circlesIds.length > 0}
      moreStatuses={{
        [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
        [ScrollDirectionsEnum.BOTTOM]: moreStatus,
      }}
      onLoadMore={onLoadMore}
      renderRow={renderRow}
      shouldRenderEmptyState={moreStatus === AsyncMoreStatusesEnum.ALL_LOADED && circles.length === 0}
      shouldAllowMultiSelect
      isInMultiSelectMode={selectedCircleIds.length !== 0}
      onMultiSelectHeaderChange={onMultiselectClick}
      renderEmpty={() => (
        <TableEmptyState headerOffset={headerOffset}>
          <Trans id="circles-table-empty">No groups</Trans>
        </TableEmptyState>
      )}
      renderMultiSelectHeader={renderMultiSelectHeader}
    />
  );
};

/*
 * Helpers.
 */

function renderHeader(
  onMultiSelectHeaderChange: HandlerOf<boolean>,
  selectedIds: ReadonlyArray<number>,
  allCircleIds: ReadonlyArray<number>,
  onDeleteClick: Handler,
) {
  const multiSelectStatus = computeMultiSelectCheckboxState(selectedIds.length, allCircleIds.length);

  return (
    <TableMultiSelectHeaderRow
      multiSelectStatus={multiSelectStatus}
      onMultiSelectChange={onMultiSelectHeaderChange}
      renderTotalItemsLabel={() => null}
    >
      <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onDeleteClick}>
        <Trans id="circles-table-bulk-delete">Delete</Trans>
      </Button>
    </TableMultiSelectHeaderRow>
  );
}
